import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ClinqLogo from "../components/ClinqLogo";
import { Plus, Trash2, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import {
  ADVISOR_TERMS_TEXT, NDA_CHECKBOX_TEXT, TERMS_CHECKBOX_TEXT, ackText,
} from "../lib/advisorTerms";

const inputCls = "mt-1.5 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-emerald-400";
const fmtErr = (d) => (typeof d === "string" ? d
  : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ")
  : "Something went wrong — please try again.");

export default function AdvisorSignup() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [clients, setClients] = useState([{ name: "", email: "" }]);
  const [ndaText, setNdaText] = useState("");
  const [ndaChecked, setNdaChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [acks, setAcks] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/nda/text").then((r) => setNdaText(r.data?.text || "")).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setClient = (i, k, v) => setClients((c) => c.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const validClients = clients.filter((c) => c.name.trim() && /\S+@\S+\.\S+/.test(c.email));

  const next1 = () => {
    setErr("");
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || form.phone.trim().length < 6 || form.password.length < 6) {
      setErr("Please fill your full name, a valid email, phone number and a password of at least 6 characters."); return;
    }
    if (validClients.length === 0) {
      setErr("Please add at least one founder client (business name + a valid email)."); return;
    }
    setStep(2);
  };

  const submit = async () => {
    setErr("");
    for (const c of validClients) {
      if (!acks[c.email]) { setErr(`Please complete the confidentiality acknowledgement for ${c.name}.`); return; }
    }
    setBusy(true);
    try {
      const res = await api.post("/advisors/register", {
        ...form,
        nda_accepted: ndaChecked,
        terms_accepted: termsChecked,
        clients: validClients.map((c) => ({ ...c, acknowledged: true })),
      });
      if (res?.data?.token) setAuthToken(res.data.token);
      await refresh();
      nav("/advisor/dashboard");
    } catch (e) {
      setErr(fmtErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#04302E] text-white" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}>
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="block"><ClinqLogo /></Link>
          <Link to="/login" className="text-sm font-semibold text-white/70 hover:text-white">Sign in</Link>
        </div>
      </nav>

      <div className="mx-auto max-w-2xl px-6 py-12" data-testid="advisor-signup">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Advisor registration · Step {step} of 4</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">
          {step === 1 && "Tell us about you and your clients"}
          {step === 2 && "Clinq Platform Non-Disclosure Agreement"}
          {step === 3 && "Clinq Advisor Terms and Conditions"}
          {step === 4 && "Client confidentiality acknowledgements"}
        </h1>

        {step === 1 && (
          <div className="mt-8 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="text-xs font-bold text-white/70">Full name</label>
                <input data-testid="advisor-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Jane Mwangi" /></div>
              <div><label className="text-xs font-bold text-white/70">Email</label>
                <input data-testid="advisor-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@firm.co.ke" /></div>
              <div><label className="text-xs font-bold text-white/70">Phone number</label>
                <input data-testid="advisor-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+254 7XX XXX XXX" /></div>
              <div><label className="text-xs font-bold text-white/70">Password</label>
                <input data-testid="advisor-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} placeholder="Min 6 characters" /></div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-bold">Founder clients you're requesting access to</p>
              <p className="mt-1 text-xs text-white/50">Each client will receive an email to authorise your access. You can add more clients later from your dashboard.</p>
              <div className="mt-3 space-y-2">
                {clients.map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <input data-testid={`client-name-${i}`} value={c.name} onChange={(e) => setClient(i, "name", e.target.value)} className={inputCls + " mt-0"} placeholder="Business name" />
                    <input data-testid={`client-email-${i}`} type="email" value={c.email} onChange={(e) => setClient(i, "email", e.target.value)} className={inputCls + " mt-0"} placeholder="founder@business.co.ke" />
                    {clients.length > 1 && (
                      <button type="button" onClick={() => setClients((cs) => cs.filter((_, j) => j !== i))} className="shrink-0 rounded-lg border border-white/10 px-2 text-white/40 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" data-testid="add-client-row" onClick={() => setClients((c) => [...c, { name: "", email: "" }])}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-300 hover:text-emerald-200">
                <Plus className="h-3.5 w-3.5" /> Add another client
              </button>
            </div>

            {err && <p className="text-sm text-rose-300" data-testid="advisor-error">{err}</p>}
            <button type="button" data-testid="advisor-step1-next" onClick={next1}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-bold text-[#04302E] hover:bg-emerald-400">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8">
            <div className="max-h-80 overflow-y-auto whitespace-pre-line rounded-xl border border-white/10 bg-white/[0.04] p-5 text-sm leading-relaxed text-white/80" data-testid="nda-text-box">
              {ndaText || "Loading NDA…"}
            </div>
            <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm">
              <input type="checkbox" data-testid="nda-checkbox" checked={ndaChecked} onChange={(e) => setNdaChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-emerald-400" />
              <span>{NDA_CHECKBOX_TEXT}</span>
            </label>
            {err && <p className="mt-3 text-sm text-rose-300">{err}</p>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 py-3 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button type="button" data-testid="advisor-step2-next" disabled={!ndaChecked}
                onClick={() => { setErr(""); setStep(3); }}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-bold text-[#04302E] hover:bg-emerald-400 disabled:opacity-40">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-8">
            <div className="max-h-80 overflow-y-auto whitespace-pre-line rounded-xl border border-white/10 bg-white/[0.04] p-5 text-sm leading-relaxed text-white/80" data-testid="terms-text-box">
              {ADVISOR_TERMS_TEXT}
            </div>
            <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm">
              <input type="checkbox" data-testid="terms-checkbox" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-emerald-400" />
              <span>{TERMS_CHECKBOX_TEXT}</span>
            </label>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setStep(2)} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 py-3 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button type="button" data-testid="advisor-step3-next" disabled={!termsChecked}
                onClick={() => setStep(4)}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-bold text-[#04302E] hover:bg-emerald-400 disabled:opacity-40">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-8">
            <p className="text-sm text-white/70">One acknowledgement per client. Each client will then receive an email to authorise your access.</p>
            <div className="mt-4 space-y-3">
              {validClients.map((c, i) => (
                <label key={c.email} className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 text-sm">
                  <input type="checkbox" data-testid={`ack-checkbox-${i}`} checked={!!acks[c.email]}
                    onChange={(e) => setAcks((a) => ({ ...a, [c.email]: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-400" />
                  <span>{ackText(c.name)}</span>
                </label>
              ))}
            </div>
            {err && <p className="mt-3 text-sm text-rose-300" data-testid="advisor-error">{err}</p>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setStep(3)} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 py-3 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button type="button" data-testid="advisor-submit" disabled={busy || validClients.some((c) => !acks[c.email])}
                onClick={submit}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-bold text-[#04302E] hover:bg-emerald-400 disabled:opacity-40">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : "Complete registration"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
